/**
 * Industry-Standard Idle Timer Hook
 * Best Practices:
 * - Visual warning before auto-logout
 * - Countdown display for user awareness
 * - Activity detection across multiple event types
 * - Debounced activity tracking (avoid excessive resets)
 * - Graceful logout with session data preservation
 * - Resumable session (optional)
 */
/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useRef, useCallback, useState } from 'react';
import { logger } from '../utils/logger';
import { SESSION_CONFIG } from '../config/constants';

interface IdleTimerConfig {
	/** Whether to enable idle timer tracking (default: true) */
	isEnabled?: boolean;
	/** Total idle time before logout (ms) */
	idleTimeout?: number;
	/** Time to show warning before logout (ms) */
	warningTime?: number;
	/** Debounce delay for activity events (ms) */
	debounceTime?: number;
	/** Callback when warning should be displayed */
	onWarning?: (timeRemaining: number) => void;
	/** Callback when session expires */
	onSessionExpire?: () => void;
	/** Callback to extend session (user clicks "stay logged in") */
	onExtendSession?: () => void;
	/** Whether to track activity across entire document */
	trackActivityGlobally?: boolean;
}

const DEFAULT_CONFIG: Required<IdleTimerConfig> = {	
	isEnabled: true,
	idleTimeout: SESSION_CONFIG.IDLE_TIMEOUT || 30000, // 30 seconds
	warningTime: SESSION_CONFIG.WARNING_DURATION || 5000, // 5 seconds
	debounceTime: 500, // Debounce activity events
	onWarning: () => {},
	onSessionExpire: () => {},
	onExtendSession: () => {},
	trackActivityGlobally: true,
};

interface IdleTimerState {
	isWarningVisible: boolean;
	timeRemaining: number;
	isActive: boolean;
}

/**
 * Advanced idle timer with visual warnings and configurable thresholds
 * @param config - Configuration options
 * @returns State object with isWarningVisible and timeRemaining
 */
export const useIdleTimer = (config: IdleTimerConfig = {}) => {
	// Use ref to store config to avoid dependency issues
	const configRef = useRef<Required<IdleTimerConfig>>({
		...DEFAULT_CONFIG,
		...config,
	});

	// Update config ref when any config value changes
	useEffect(() => {
		configRef.current = { ...DEFAULT_CONFIG, ...config };
	}, [
		config.isEnabled,
		config.idleTimeout,
		config.warningTime,
		config.debounceTime,
		config.onWarning,
		config.onSessionExpire,
		config.onExtendSession,
		config.trackActivityGlobally,
	]);

	const [state, setState] = useState<IdleTimerState>({
		isWarningVisible: false,
		timeRemaining: configRef.current.idleTimeout,
		isActive: true,
	});

	const timersRef = useRef<{
		idleTimer: NodeJS.Timeout | null;
		warningTimer: NodeJS.Timeout | null;
		countdownTimer: NodeJS.Timeout | null;
		lastActivityTime: number;
		activityDebounce: NodeJS.Timeout | null;
	}>({
		idleTimer: null,
		warningTimer: null,
		countdownTimer: null,
		lastActivityTime: Date.now(),
		activityDebounce: null,
	});

	/**
	 * Clear all active timers
	 */
	const clearAllTimers = useCallback(() => {
		const { idleTimer, warningTimer, countdownTimer, activityDebounce } =
			timersRef.current;

		if (idleTimer) clearTimeout(idleTimer);
		if (warningTimer) clearTimeout(warningTimer);
		if (countdownTimer) clearTimeout(countdownTimer);
		if (activityDebounce) clearTimeout(activityDebounce);

		timersRef.current.idleTimer = null;
		timersRef.current.warningTimer = null;
		timersRef.current.countdownTimer = null;
		timersRef.current.activityDebounce = null;
	}, []);

	/**
	 * Start countdown timer that updates every second
	 */
	const startCountdown = useCallback((initialTime: number) => {
		let timeLeft = initialTime;

		timersRef.current.countdownTimer = setInterval(() => {
			timeLeft -= 1000;

			if (timeLeft <= 0) {
				if (timersRef.current.countdownTimer) {
					clearInterval(timersRef.current.countdownTimer);
				}
				setState((prev) => ({
...prev,
timeRemaining: 0,
}));
			} else {
				setState((prev) => ({
...prev,
timeRemaining: timeLeft,
}));

				// Call warning callback with remaining time
				configRef.current.onWarning(timeLeft);
			}
		}, 1000);
	}, []);

	/**
	 * Initialize or reset the idle timer
	 */
	const resetIdleTimer = useCallback(() => {
		clearAllTimers();

		// Update last activity time
		timersRef.current.lastActivityTime = Date.now();

		// Set warning timer
		const warningDelay =
			configRef.current.idleTimeout - configRef.current.warningTime;

		timersRef.current.warningTimer = setTimeout(() => {
			logger.warn(
`⏰ Idle timeout warning: Session will expire in ${configRef.current.warningTime / 1000}s`
);

			setState((prev) => ({
...prev,
isWarningVisible: true,
timeRemaining: configRef.current.warningTime,
}));

			// Start countdown
			startCountdown(configRef.current.warningTime);
		}, warningDelay);

		// Set logout timer
		timersRef.current.idleTimer = setTimeout(() => {
			logger.warn('❌ Idle timeout - Session expired');

			setState((prev) => ({
...prev,
isWarningVisible: false,
isActive: false,
}));

			clearAllTimers();
			configRef.current.onSessionExpire();
		}, configRef.current.idleTimeout);

		// Reset state when activity detected
		setState((prev) => ({
...prev,
isWarningVisible: false,
timeRemaining: configRef.current.idleTimeout,
isActive: true,
}));
	}, [clearAllTimers, startCountdown]);

	/**
	 * Handle activity with debouncing to avoid excessive resets
	 */
	const handleActivity = useCallback(() => {
		if (timersRef.current.activityDebounce) {
			clearTimeout(timersRef.current.activityDebounce);
		}

		// Debounce activity detection
		timersRef.current.activityDebounce = setTimeout(() => {
			const now = Date.now();
			const timeSinceLastActivity = now - timersRef.current.lastActivityTime;

			// Only reset if enough time has passed
			if (timeSinceLastActivity >= configRef.current.debounceTime) {
				logger.debug('👤 User activity detected - idle timer reset');
				resetIdleTimer();
			}
		}, configRef.current.debounceTime);
	}, [resetIdleTimer]);

	/**
	 * Extend session (user clicks "stay logged in" button)
	 */
	const extendSession = useCallback(() => {
		logger.info('✅ Session extended by user action');
		setState((prev) => ({
...prev,
isWarningVisible: false,
timeRemaining: configRef.current.idleTimeout,
isActive: true,
}));
		resetIdleTimer();
		configRef.current.onExtendSession();
	}, [resetIdleTimer]);

	/**
	 * Force logout immediately
	 */
	const forceLogout = useCallback(() => {
		logger.warn('🚫 Force logout requested');
		clearAllTimers();
		setState((prev) => ({
...prev,
isWarningVisible: false,
isActive: false,
}));
		configRef.current.onSessionExpire();
	}, [clearAllTimers]);

	/**
	 * Get time remaining in MM:SS format
	 */
	const getFormattedTime = useCallback((): string => {
		const totalSeconds = Math.floor(state.timeRemaining / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${seconds
.toString()
.padStart(2, '0')}`;
	}, [state.timeRemaining]);

	// Setup activity listeners and initialize timer - ONLY when enabled
	useEffect(() => {
		// Skip if not enabled
		if (!configRef.current.isEnabled) {
			clearAllTimers();
			// Reset state when disabled
			setState((prev) => ({
				...prev,
				isWarningVisible: false,
				isActive: true,
			}));
			return;
		}


		const activityEvents = [
			'mousedown',
			'mousemove',
			'keypress',
			'keydown',
			'scroll',
			'touchstart',
			'touchmove',
			'click',
			'dblclick',
			'focus',
			'wheel',
		];

		const captureEvent = () => {
			handleActivity();
		};

		// Add event listeners
		if (configRef.current.trackActivityGlobally) {
			activityEvents.forEach((eventName) => {
				document.addEventListener(eventName, captureEvent, true);
			});
		}

		// Initialize idle timer
		resetIdleTimer();

		// Cleanup
		return () => {
			activityEvents.forEach((eventName) => {
				document.removeEventListener(eventName, captureEvent, true);
			});
			clearAllTimers();
		};
	}, [handleActivity, resetIdleTimer, clearAllTimers]);

	return {
		...state,
		timeRemaining: state.timeRemaining,
		formattedTime: getFormattedTime(),
		extendSession,
		forceLogout,
		resetTimer: resetIdleTimer,
	};
};

export type IdleTimerReturn = ReturnType<typeof useIdleTimer>;
